import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Button, Paper, Typography, Snackbar, Alert, TextField, Grid2 } from "@mui/material";
import { Iconify } from "../Minimal/src/components/iconify/iconify";
import AddProductDialog from "../sales/EposEdit/component/AddProductDialog";
import BarcodeScanner from "../common/BarcodeScanner";
import { ConfirmDialog } from "../common/confirm-dialog";
import { useQuery, useMutation } from "@tanstack/react-query";
import CartTable from "../sales/Epos/component/CartTable";
import CartSummary from "../sales/Epos/component/CartSummary";
import { fetchPlaceOrder, updateInvoice, checkProductPromotion, getProductsList } from "../../data/epos";
import { transformCartToPromotionPayload, mergePromotionResponseWithCart, formatDateForAPI, transformProductDetailsToCart } from "../sales/EposEdit/utils/promotionHelpers";
import { determineItemType, parseQty } from "../sales/Epos/utils/cartCalculations";
import { getCompanySettings, resolveCheckStockBeforeAddToCart } from "../../utils/companySettingsCache";
import "../common/commonStyles.css";

const EditProforma = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [orderHeader, setOrderHeader] = useState(null);
    const [orderChannel, setOrderChannel] = useState("pos");
    const [totalInvDiscPercent, setTotalInvDiscPercent] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [staffNote, setStaffNote] = useState("");
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
    const [cancelConfirmDialog, setCancelConfirmDialog] = useState(false);
    const [deleteItemDialog, setDeleteItemDialog] = useState({ open: false, itemId: null });
    const [cartItems, setCartItems] = useState([]);
    const cartItemsRef = useRef([]);
    const [originalOrderProducts, setOriginalOrderProducts] = useState([]);
    const [customerId, setCustomerId] = useState(null);
    const [promotionCheckQueue, setPromotionCheckQueue] = useState({});
    const promotionCheckTimerRef = useRef(null);
    const [cartTotals, setCartTotals] = useState({
        boxCount: 0,
        pieceCount: 0,
        subtotal: 0,
        vat: 0,
        stdGoods: 0,
        zeroGoods: 0,
        totalBeforeDiscount: 0,
        discountAmount: 0,
        promoDiscPercent: 0,
        grandTotal: 0,
        promoVatAmount:0,
        promoDiscountAmount:0,
        over_all_discount:0
    });
    const [apiTotals, setApiTotals] = useState(null);
    const allotQtyDebounceTimerRef = useRef(null);
    const lastDiscountCalculationRef = useRef({ totalDiscountAmount: 0, itemsHash: "" });
    const isBarcodeProcessingRef = useRef(false);
    const isSingleOrderPickingRef = useRef(false);
    const [shouldCheckStockBeforeAddToCart, setShouldCheckStockBeforeAddToCart] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const loadCompanySettings = async () => {
            const settings = await getCompanySettings();
            if (!isMounted) return;
            setShouldCheckStockBeforeAddToCart(resolveCheckStockBeforeAddToCart(settings));
            isSingleOrderPickingRef.current = settings?.isSingleOrderPicking === "Y";
        };

        loadCompanySettings();

        return () => {
            isMounted = false;
        };
    }, []);

    const { data: orderData, isLoading, isError } = useQuery({
        queryKey: ["editEposOrder", orderId],
        queryFn: () => fetchPlaceOrder(orderId),
        enabled: !!orderId,
    });

    const checkPromotionMutation = useMutation({
        mutationFn: checkProductPromotion,
        onSuccess: (data) => {
            if (Array.isArray(data?.data?.items)) {
                const currentCartItems = cartItemsRef.current;
                const cartDataForMerge = {
                    data: currentCartItems.length > 0
                        ? currentCartItems
                        : transformProductDetailsToCart(originalOrderProducts),
                    success: true
                };
                const mergedItems = mergePromotionResponseWithCart(cartDataForMerge, data, originalOrderProducts, currentCartItems, isSingleOrderPickingRef.current);
                setCartItems(mergedItems);

                if (data?.data?.totals) {
                    setApiTotals({
                        ...data.data.totals,
                        bundle_discounts: data.data.bundle_discounts
                    });
                }
            }
        },
        onError: (error) => {
            setSnackbar({ open: true, message: "Failed to check promotions", severity: "error" });
        },
    });

    const updateInvoiceMutation = useMutation({
        mutationFn: updateInvoice,
        onSuccess: (data) => {
            if (data?.success) {
                setSnackbar({ open: true, message: "Invoice updated successfully", severity: "success" });
                setTimeout(() => {
                    navigate('/delivery/route_planing');
                }, 1000);
            } else {
                setSnackbar({ open: true, message: data?.message || "Failed to update invoice", severity: "error" });
            }
        },
        onError: (error) => {
            setSnackbar({ open: true, message: error.message || "Failed to update invoice", severity: "error" });
        },
    });

    const getItemDetails = useCallback((item) => {
        return determineItemType(item);
    }, []);

    const calculateProportionalInvoiceDiscount = useCallback((items, totalDiscountAmount) => {
        if (!items || items.length === 0 || totalDiscountAmount === 0) {
            return items.map(item => ({ ...item, invoiceDiscountAmount: 0 }));
        }

        let totalContribution = 0;
        const itemContributions = items.map((item) => {
            const price = parseFloat(item.price) || 0;
            const qty = parseInt(item.qty) || 0;
            const discount = parseFloat(item.discount || 0);
            const vatRate = parseFloat(item.rate || item.tax_rate || 0);
            const taxMethod = item.tax_method || "exclusive";
            const isVatInclusive = taxMethod === "inclusive";

            const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
            const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
            const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

            let baseTotal = price * qty;

            let discountedTotal;
            if (isFixedDiscount) {
                const discountAmount = parseFloat(discount) || 0;
                discountedTotal = Math.max(0, baseTotal - discountAmount);
            } else {
                const multiplier = Math.min(Math.max(discount, 0), 100);
                discountedTotal = baseTotal * (1 - multiplier / 100);
            }

            let subtotal;
            let vat;
            if (isVatInclusive && vatRate > 0) {
                subtotal = discountedTotal / (1 + vatRate / 100);
                vat = discountedTotal - subtotal;
            } else {
                subtotal = discountedTotal;
                vat = subtotal > 0 ? (subtotal * vatRate) / 100 : 0;
            }

            const itemTotalBeforeInvoiceDiscount = subtotal + vat;
            totalContribution += itemTotalBeforeInvoiceDiscount;

            return {
                itemId: item.id || item.product_id,
                contribution: itemTotalBeforeInvoiceDiscount
            };
        });

        if (totalContribution === 0) {
            return items.map(item => ({ ...item, invoiceDiscountAmount: 0 }));
        }

        return items.map((item) => {
            const contributionData = itemContributions.find(c =>
                c.itemId === (item.id || item.product_id)
            );

            if (!contributionData) {
                return { ...item, invoiceDiscountAmount: 0 };
            }

            const contributionRatio = contributionData.contribution / totalContribution;
            const proportionalDiscountAmount = totalDiscountAmount * contributionRatio;

            return {
                ...item,
                invoiceDiscountAmount: proportionalDiscountAmount
            };
        });
    }, []);

    const calculateCartTotals = useCallback(() => {
        if (apiTotals && cartItems.length > 0 && cartItems.some(item => item.useBackendCalculations)) {
            let boxCount = 0;
            let pieceCount = 0;
            let stdGoods = 0;
            let zeroGoods = 0;

            cartItems.forEach((item) => {
                const orderType = item.type || item.order_type || "Box";
                const qty = parseInt(item.qty) || 0;

                if (orderType === "Box") {
                    boxCount += qty;
                } else {
                    pieceCount += qty;
                }

                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";
                const lineSubtotal = parseFloat(item.line_subtotal || item.subtotal || item.sub_total || 0);
                const lineVat = parseFloat(item.line_vat || item.line_tax || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);

                const itemSubtotalForGoods = isVatInclusive && lineVat > 0
                    ? (lineSubtotal - lineVat)
                    : lineSubtotal;

                if (itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            });

            const backendSubtotal = parseFloat(apiTotals.sub_total ?? apiTotals.subtotal ?? 0);
            const vatTotal = cartItems.reduce((sum, item) => {
                const lineVat = parseFloat(item.line_vat ?? item.line_tax ?? item.vat ?? item.tax ?? 0);
                return sum + (Number.isFinite(lineVat) ? Math.max(0, lineVat) : 0);
            }, 0);
            const normalizedVatTotal = parseFloat(vatTotal.toFixed(2));
            const totalBeforeDiscount = backendSubtotal + normalizedVatTotal;

            const discountAmount = Math.max(0, (totalBeforeDiscount * (parseFloat(totalInvDiscPercent) || 0)) / 100);
            const bundleDiscounts = Array.isArray(apiTotals.bundle_discounts) ? apiTotals.bundle_discounts : [];
            let promoDiscountAmount = 0;
            let promoVatAmount = 0;
            const hasPromotionAppliedOnAnyItem = cartItems.some((it) => {
                const isPromoted = it.is_promoted === "1" || it.is_promoted === 1;
                const promotionApplied = it.promotion_applied === true || it.promotion_applied === "true" || it.promotion_applied === 1 || it.promotion_applied === "1";
                const promoId =
                    it.promo_id ?? it.promoId ?? it.promotion_id ?? it.promotionId ?? it.promosId ?? it.promos_id ?? 0;
                const promoIdNum = Number(promoId);
                return isPromoted || promotionApplied || (Number.isFinite(promoIdNum) && promoIdNum > 0);
            });
            bundleDiscounts.forEach((b) => {
                promoDiscountAmount += parseFloat(b.buy_total_discount || 0) + parseFloat(b.get_total_discount || 0);
                promoVatAmount += parseFloat(b.buy_discount_vat || 0) + parseFloat(b.get_discount_vat || 0);
            });
            if (promoDiscountAmount < 0) {
                promoDiscountAmount = 0;
            }
            if (promoVatAmount < 0) {
                promoVatAmount = 0;
            }
            const gross = parseFloat((stdGoods + zeroGoods + normalizedVatTotal).toFixed(2));
            let grandTotal = totalBeforeDiscount;
            const hasBundleDiscounts = bundleDiscounts.length > 0;
            if (hasBundleDiscounts) {
                grandTotal = Math.max(0, gross - discountAmount - promoDiscountAmount - promoVatAmount);
            } else {
                const backendGrandTotal =
                    parseFloat(apiTotals.grand_total ?? apiTotals.grandTotal ?? 0) || 0;
                grandTotal = Math.max(0, backendGrandTotal);
            }
            const promoDiscPercent =
                totalBeforeDiscount > 0
                    ? (promoDiscountAmount / totalBeforeDiscount) * 100
                    : 0;

            setCartTotals({
                boxCount,
                pieceCount,
                subtotal: backendSubtotal,
                vat: normalizedVatTotal,
                stdGoods,
                zeroGoods,
                gross,
                totalBeforeDiscount,
                discountAmount,
               promoDiscountAmount,
                promoVatAmount,
                promoDiscPercent,
                grandTotal,
                over_all_discount: parseFloat(apiTotals.over_all_discount ?? apiTotals.overAllDiscount ?? 0) || 0,
            });
            return;
        }

        let boxCount = 0;
        let pieceCount = 0;
        let stdGoods = 0;
        let zeroGoods = 0;
        let subtotalAmount = 0;
        let vatAmount = 0;

        cartItems.forEach((item) => {
            const orderType = item.type || item.order_type || "Box";
            const qty = parseInt(item.qty) || 0;

            if (orderType === "Box") {
                boxCount += qty;
            } else {
                pieceCount += qty;
            }

            if (item.useBackendCalculations) {
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";
                const lineSubtotal = parseFloat(item.line_subtotal || item.subtotal || item.sub_total || 0);
                const lineVat = parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);

                if (isVatInclusive) {
                    subtotalAmount += lineSubtotal;
                    vatAmount += lineVat;
                } else {
                    subtotalAmount += lineSubtotal;
                    vatAmount += lineVat;
                }

                const itemSubtotalForGoods = isVatInclusive ? (lineSubtotal - lineVat) : lineSubtotal;
                if (itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            } else {
                const price = parseFloat(item.price) || 0;
                const discount = parseFloat(item.discount || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";

                const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

                let baseTotal = price * qty;

                let discountedTotal;
                if (isFixedDiscount) {
                    const discountAmount = parseFloat(discount) || 0;
                    discountedTotal = Math.max(0, baseTotal - discountAmount);
                } else {
                    const multiplier = Math.min(Math.max(discount, 0), 100);
                    discountedTotal = baseTotal * (1 - multiplier / 100);
                }

                let itemSubtotal;
                let itemVat;
                if (isVatInclusive && vatRate > 0) {
                    itemSubtotal = discountedTotal / (1 + vatRate / 100);
                    itemVat = discountedTotal - itemSubtotal;
                } else {
                    itemSubtotal = discountedTotal;
                    itemVat = itemSubtotal > 0 ? (itemSubtotal * vatRate) / 100 : 0;
                }

                subtotalAmount += itemSubtotal;
                vatAmount += itemVat;

                if (itemSubtotal > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotal;
                    } else {
                        zeroGoods += itemSubtotal;
                    }
                }
            }
        });

        const totalBeforeDiscount = subtotalAmount + vatAmount;
        const totalInvoiceDiscountAmount = (totalBeforeDiscount * totalInvDiscPercent) / 100;
        const gross = totalBeforeDiscount;
        const grandTotal = Math.max(0, totalBeforeDiscount - totalInvoiceDiscountAmount);

        setCartTotals({
            boxCount,
            pieceCount,
            subtotal: subtotalAmount,
            vat: vatAmount,
            stdGoods,
            zeroGoods,
            gross,
            totalBeforeDiscount,
            discountAmount: totalInvoiceDiscountAmount,
            promoDisc: 0,
            promoVat: 0,
            promoDiscPercent: 0,
            grandTotal
        });
    }, [cartItems, totalInvDiscPercent, apiTotals]);

    const handleQuantityChange = useCallback((cartItem, value, changeType = 'absolute') => {
        const itemId = cartItem.id || cartItem.product_id;

        setCartItems((prev) => {
            const sourceItem = prev.find(item => (item.id === itemId || item.product_id === itemId));
            if (!sourceItem) return prev;

            const currentQty = parseInt(sourceItem.qty) || 0;
            let delta;

            if (changeType === 'increment' || changeType === 'decrement') {
                delta = value;
            } else {
                const targetQty = parseInt(value);
                if (!Number.isFinite(targetQty)) return prev;
                delta = targetQty - currentQty;
                if (delta === 0) return prev;
            }

            // Check if this is an offer product (generated product) with get_quantity limit
            const isOfferProduct = sourceItem.generated === true || sourceItem.generated === "1" || sourceItem.generated === 1;
            const promosId = sourceItem.promosId || sourceItem.promoId || 0;
            const getQuantity = sourceItem.get_quantity ? parseInt(sourceItem.get_quantity) : null;

            if (isOfferProduct && getQuantity !== null && delta > 0) {
                // Calculate sum of all offer products for the same promotion
                const totalOfferQty = prev.reduce((sum, item) => {
                    const itemIsOffer = item.generated === true || item.generated === "1" || item.generated === 1;
                    const itemPromosId = item.promosId || item.promoId || 0;

                    if (itemIsOffer && itemPromosId === promosId) {
                        if (item.id === itemId || item.product_id === itemId) {
                            // Include the new quantity for the current item
                            return sum + (parseInt(item.qty) || 0) + delta;
                        } else {
                            // Include existing quantity for other items
                            return sum + (parseInt(item.qty) || 0);
                        }
                    }
                    return sum;
                }, 0);

                if (totalOfferQty > getQuantity) {
                    setSnackbar({
                        open: true,
                        message: `Cannot increase quantity. Total offer products quantity (${totalOfferQty}) exceeds the maximum allowed (${getQuantity}).`,
                        severity: "warning"
                    });
                    return prev;
                }
            }

            return prev.map((item) => {
                if (item.id === itemId || item.product_id === itemId) {
                    const newQty = parseInt(item.qty) + delta;
                    return {
                        ...item,
                        qty: newQty
                    };
                }
                return item;
            });
        });
    }, []);

    const handleTypeChange = useCallback((cartItem, newType) => {
        const itemId = cartItem.id || cartItem.product_id;
        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId) {
                const newPrice = newType === "Box"
                    ? (parseFloat(item.sales_box_price || item.boxPrice || item.price) || 0)
                    : (parseFloat(item.sales_piece_price || item.piecePrice || item.piece_price1) || 0);

                return {
                    ...item,
                    type: newType,
                    order_type: newType,
                    price: newPrice
                };
            }
            return item;
        }));
    }, []);

    const promotionCheckInProgressRef = useRef(false);

    const handlePriceChange = useCallback((cartItem, value, options = {}) => {
        const { commit = true } = options;
        const itemId = cartItem.id || cartItem.product_id;
        const parsedValue = commit ? parseFloat(value) : value;
        const numForUnitPrice = Number.isFinite(parseFloat(value)) ? parseFloat(value) : null;

        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId) {
                const update = { ...item, price: parsedValue };
                if (commit && Number.isFinite(parsedValue)) {
                    update.unit_price = parsedValue;
                } else if (numForUnitPrice !== null) {
                    update.unit_price = numForUnitPrice;
                }
                return update;
            }
            return item;
        }));
    }, []);

    const handlePriceChangeComplete = useCallback(() => {
        if (!customerId || promotionCheckInProgressRef.current) return;
        const currentCartItems = cartItemsRef.current;
        if (currentCartItems.length === 0) return;
        const productIds = currentCartItems.map(item => item.product_id || item.productId).filter(Boolean);
        const uniqueProductIds = [...new Set(productIds)];
        const newQueue = {};
        uniqueProductIds.forEach(pid => {
            newQueue[pid] = Date.now();
        });
        setPromotionCheckQueue(prevQueue => ({ ...prevQueue, ...newQueue }));
    }, [customerId]);

    const handleDiscountChange = useCallback((cartItem, value, discountType = "amount") => {
        const itemId = cartItem.id || cartItem.product_id;
        const parsedValue = parseFloat(value);
        const discountValue = Number.isFinite(parsedValue) ? parsedValue : 0;
        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId || item.product_id === itemId) {
                return {
                    ...item,
                    discount: discountValue,
                    discount_type: discountType,
                    productWiseDiscountType: discountType
                };
            }
            return item;
        }));
    }, []);

    const handleDeleteItem = useCallback((itemId) => {
        setCartItems((prev) => {
            const updatedItems = prev.filter((item) => item.id !== itemId && item.product_id !== itemId);

            if (customerId && updatedItems.length > 0) {
                const productIds = updatedItems.map(item => item.product_id || item.productId).filter(Boolean);
                const uniqueProductIds = [...new Set(productIds)];
                const newQueue = {};
                uniqueProductIds.forEach(pid => {
                    newQueue[pid] = Date.now();
                });
                setPromotionCheckQueue(prevQueue => ({ ...prevQueue, ...newQueue }));
            }

            return updatedItems;
        });
    }, [customerId]);

    const clearCart = useCallback(() => {
        setCartItems([]);
    }, []);

    useEffect(() => {
        if (!orderData) return;
        if (!orderData?.success) {
            setSnackbar({ open: true, message: orderData?.message || "Failed to load order", severity: "error" });
            return;
        }
        const details = orderData?.details || {};
        const order = Array.isArray(details?.orderDetails) ? details.orderDetails[0] : null;
        const products = Array.isArray(details?.product_details) ? details.product_details : [];

        setOrderHeader(order || null);
        setSelectedCustomer(order ? {
            order_id: order?.id,
            id: order?.customer_id,
            name: order?.name,
            company: order?.company || "",
            address: order?.delivery_address || ""
        } : null);

        if (order?.customer_id) {
            setCustomerId(order.customer_id);
        }

        if (order?.channel) {
            setOrderChannel(order.channel);
        }

        setOriginalOrderProducts(products);

        const orderDiscount = Number(order?.order_discount ?? 0) || 0;
        setTotalInvDiscPercent(orderDiscount);
        setDeliveryAddress(order?.delivery_address || order?.address || "");
        setStaffNote(order?.staff_note || "");
    }, [orderData]);

    useEffect(() => {
        if (!customerId) return;

        if (!originalOrderProducts?.length) {
            setSnackbar({ open: true, message: "No products found for this order", severity: "info" });
            setCartItems([]);
            return;
        }

        let cancelled = false;

        (async () => {
            const settings = await getCompanySettings();
            if (cancelled) return;
            isSingleOrderPickingRef.current = settings?.isSingleOrderPicking === "Y";

            const transformedProducts = transformProductDetailsToCart(originalOrderProducts);
            if (cancelled) return;
            setCartItems([]);

            const cartDataToUse = {
                data: transformedProducts,
                success: true
            };

            const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
            const orderId = orderHeader?.order_id ?? orderHeader?.id ?? null;
            const payload = transformCartToPromotionPayload(cartDataToUse, customerId, orderDate, orderChannel, orderTime, orderId);
            payload.over_all_discount = (orderHeader?.order_discount != null ? Number(orderHeader.order_discount) : totalInvDiscPercent) ?? 0;
            if (cancelled) return;
            checkPromotionMutation.mutate(payload);
        })();

        return () => {
            cancelled = true;
        };
    }, [customerId, originalOrderProducts, orderHeader, orderChannel]);

    useEffect(() => {
        if (!customerId || cartItemsRef.current.length === 0) return;
        setPromotionCheckQueue((prev) => ({ ...prev, overall_discount: Date.now() }));
    }, [totalInvDiscPercent]);

    useEffect(() => {
        if (Object.keys(promotionCheckQueue).length === 0) return;
        if (!customerId) return;
        if (promotionCheckInProgressRef.current) return;

        if (promotionCheckTimerRef.current) {
            clearTimeout(promotionCheckTimerRef.current);
        }

        promotionCheckTimerRef.current = setTimeout(() => {
            promotionCheckInProgressRef.current = true;
            const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);

            const itemsMap = new Map();
            const processedProductKeys = new Set();

            cartItemsRef.current.forEach(item => {
                const rawId = item.product_id ?? item.productId;
                const normalizedId = Number.isFinite(Number(rawId)) ? Number(rawId) : rawId;
                const quantity = item.allot_qty !== undefined && item.allot_qty !== null && item.allot_qty !== ""
                    ? parseFloat(item.allot_qty) || 0
                    : parseFloat(item.qty) || 0;
                const orderQty = item.order_qty !== undefined && item.order_qty !== null && item.order_qty !== ""
                    ? parseFloat(item.order_qty)
                    : null;
                const type = item.order_type || item.type || "Box";
                const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;
                const promosId = item.promosId || item.promoId || 0;
                const buyOrGet = item.buyOrGet || null;
                const promoTypeForKey = item.promo_type || (buyOrGet ? (buyOrGet.toUpperCase() === "BUY" ? "BUY" : buyOrGet.toUpperCase() === "GET" ? "GET" : buyOrGet.toUpperCase() === "FREE" ? "FREE" : "NONE") : "NONE");
                const key = `${normalizedId}_${type}_${isPromoted ? "1" : "0"}_${promosId}_${promoTypeForKey}`;
                processedProductKeys.add(`${normalizedId}_${type}`);

                if (itemsMap.has(key)) {
                    const existing = itemsMap.get(key);
                    existing.quantity += quantity;
                    const incomingPrice = parseFloat(item.unit_price ?? item.price ?? 0);
                    if (Number.isFinite(incomingPrice) && incomingPrice > 0) {
                        existing.unit_price = incomingPrice;
                    }
                    if (orderQty !== null && existing.order_qty === undefined) {
                        existing.order_qty = orderQty;
                    }
                    let promoType = item.promo_type || null;
                    if (promoType !== "BUY" && promoType !== "GET" && promoType !== "FREE") {
                        promoType = buyOrGet ? (String(buyOrGet).toUpperCase() === "BUY" ? "BUY" : String(buyOrGet).toUpperCase() === "GET" ? "GET" : String(buyOrGet).toUpperCase() === "FREE" ? "FREE" : null) : null;
                    }
                    if (promoType !== null && promoType !== undefined && (promoType === "BUY" || promoType === "GET" || promoType === "FREE") && existing.promo_type === undefined) {
                        existing.promo_type = promoType;
                    }
                    if ((existing.category_name == null || existing.category_name === "") && (item.category_name != null && item.category_name !== "")) {
                        existing.category_name = String(item.category_name).trim();
                    } else if ((existing.category_name == null || existing.category_name === "") && (item.categorie_name != null && item.categorie_name !== "")) {
                        existing.category_name = String(item.categorie_name).trim();
                    } else if ((existing.category_name == null || existing.category_name === "") && originalOrderProducts && originalOrderProducts.length > 0) {
                        const orig = originalOrderProducts.find(p => String(p.product_id || p.id) === String(normalizedId) && (p.order_type || p.type || "Box") === type);
                        if (orig && (orig.category_name != null && orig.category_name !== "")) {
                            existing.category_name = String(orig.category_name).trim();
                        } else if (orig && (orig.categorie_name != null && orig.categorie_name !== "")) {
                            existing.category_name = String(orig.categorie_name).trim();
                        }
                    }
                } else {
                    const currentPrice = parseFloat(item.unit_price ?? item.price ?? 0);
                    const originalPrice = parseFloat(item.original_price ?? (type === "Box" ? item.sales_box_price : item.sales_piece_price) ?? 0);
                    const discountPrice = parseFloat(item.discount_price ?? 0);
                    const isContractPriceApplied = item.isContractPriceApplied === "Y" || item.is_contract_price_applied === "Y";
                    
                    const itemDiscountValue = item.item_discount !== undefined && item.item_discount !== null && item.item_discount !== ""
                        ? parseFloat(item.item_discount)
                        : null;
                    const discountValue = item.discount !== undefined && item.discount !== null && item.discount !== ""
                        ? parseFloat(item.discount)
                        : null;
                    
                    let itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
                    const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                    const discountTypeForAPI = (normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount") ? "amount" : "percentage";
                    
                    const itemDiscount = discountValue !== null
                        ? discountValue
                        : (itemDiscountValue !== null && itemDiscountValue >= 0)
                            ? itemDiscountValue
                            : 0;

                    let unitPrice = currentPrice;
                    if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
                        if (isPromoted && discountPrice > 0 && discountPrice < originalPrice) {
                            unitPrice = originalPrice;
                        } else if (isPromoted && (discountPrice === 0 || originalPrice === 0)) {
                            unitPrice = originalPrice;
                        }
                    }

                    let promoType = item.promo_type || null;
                    if (promoType !== "BUY" && promoType !== "GET" && promoType !== "FREE") {
                        promoType = buyOrGet ? (String(buyOrGet).toUpperCase() === "BUY" ? "BUY" : String(buyOrGet).toUpperCase() === "GET" ? "GET" : String(buyOrGet).toUpperCase() === "FREE" ? "FREE" : null) : null;
                    }

                    const itemPayload = {
                        product_id: normalizedId,
                        piece_type: type,
                        quantity: quantity,
                        unit_price: Number.isFinite(unitPrice) ? unitPrice : 0,
                        original_price: Number.isFinite(originalPrice) ? originalPrice : 0,
                        promosId: Number.isFinite(Number(promosId)) ? Number(promosId) : 0,
                        is_contra: isContractPriceApplied ? "Y" : "N",
                        item_discount_type: discountTypeForAPI
                    };
                    const effectiveDiscount = discountValue !== null ? discountValue : itemDiscount;
                    // Only set 100% discount to "0" for promotional discounts, not manual discounts
                    const isManualDiscount = discountValue !== null;
                    const hasPromotionApplied = item.promotion_applied === true || promoType !== null || promosId > 0;
                    const isPromo100Percent = !isManualDiscount && hasPromotionApplied && discountTypeForAPI === "percentage" && (effectiveDiscount === 100 || effectiveDiscount === 100.0);
                    if (isPromo100Percent) {
                        itemPayload.item_discount = "0";
                    } else {
                        itemPayload.item_discount = String(effectiveDiscount);
                    }
                    if (orderQty !== null) {
                        itemPayload.order_qty = orderQty;
                    }
                    if (promoType !== null && (promoType === "BUY" || promoType === "GET" || promoType === "FREE")) {
                        itemPayload.promo_type = promoType;
                    }
                    const promoDiscAmount = item.promo_disc_amount !== undefined && item.promo_disc_amount !== null
                        ? parseFloat(item.promo_disc_amount)
                        : null;
                    if (promoDiscAmount !== null && Number.isFinite(promoDiscAmount)) {
                        itemPayload.promo_disc_amount = promoDiscAmount;
                    }
                    if (item.promotion_applied === true) {
                        itemPayload.promotion_applied = true;
                    }
                    const promotionId = item.promotion_id !== undefined && item.promotion_id !== null
                        ? Number(item.promotion_id)
                        : null;
                    if (promotionId !== null && Number.isFinite(promotionId) && promotionId > 0) {
                        itemPayload.promotion_id = promotionId;
                    }
                    let categoryName = (item.category_name != null && item.category_name !== "") ? String(item.category_name).trim() : ((item.categorie_name != null && item.categorie_name !== "") ? String(item.categorie_name).trim() : "");
                    if (!categoryName && originalOrderProducts && originalOrderProducts.length > 0) {
                        const orig = originalOrderProducts.find(p => String(p.product_id || p.id) === String(normalizedId) && (p.order_type || p.type || "Box") === type);
                        if (orig) {
                            categoryName = (orig.category_name != null && orig.category_name !== "") ? String(orig.category_name).trim() : ((orig.categorie_name != null && orig.categorie_name !== "") ? String(orig.categorie_name).trim() : "");
                        }
                    }
                    if (categoryName) {
                        itemPayload.category_name = categoryName;
                    }
                    itemsMap.set(key, itemPayload);
                }
            });

            if (originalOrderProducts && originalOrderProducts.length > 0) {
                originalOrderProducts.forEach(item => {
                    const buyOrGet = item.buyOrGet || null;
                    const buyOrGetUpper = buyOrGet ? String(buyOrGet).toUpperCase() : null;
                    const isFreeProduct = buyOrGetUpper === "FREE";
                    const promosId = item.promosId || item.promo_id || 0;
                    
                    if (isFreeProduct && promosId > 0) {
                        const rawId = item.product_id ?? item.id;
                        const normalizedId = Number.isFinite(Number(rawId)) ? Number(rawId) : rawId;
                        const type = item.order_type || "Box";
                        const productKey = `${normalizedId}_${type}`;
                        
                        if (!processedProductKeys.has(productKey)) {
                            const quantity = item.quantity !== undefined && item.quantity !== null && item.quantity !== ""
                                ? parseFloat(item.quantity) || 0
                                : parseFloat(item.qty) || 0;
                            const orderQty = item.order_qty !== undefined && item.order_qty !== null && item.order_qty !== ""
                                ? parseFloat(item.order_qty)
                                : null;
                            const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;
                            const key = `${normalizedId}_${type}_${isPromoted ? "1" : "0"}_${promosId}_FREE`;
                            
                            if (!itemsMap.has(key)) {
                                const unitPrice = parseFloat(item.unit_price ?? item.price ?? item.original_price ?? 0);
                                const isContractPriceApplied = item.isContractPriceApplied === "Y" || item.is_contract_price_applied === "Y";
                                const itemDiscountValue = item.item_discount !== undefined && item.item_discount !== null && item.item_discount !== ""
                                    ? parseFloat(item.item_discount)
                                    : null;
                                const discountValue = item.discount !== undefined && item.discount !== null && item.discount !== ""
                                    ? parseFloat(item.discount)
                                    : null;
                                
                                let itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
                                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                                const discountTypeForAPI = (normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount") ? "amount" : "percentage";
                                
                                const itemDiscount = discountValue !== null
                                    ? discountValue
                                    : (itemDiscountValue !== null && itemDiscountValue >= 0)
                                        ? itemDiscountValue
                                        : 0;

                                const origPriceFree = parseFloat(item.original_price ?? (type === "Box" ? item.sales_box_price : item.sales_piece_price) ?? 0);
                                const itemPayload = {
                                    product_id: normalizedId,
                                    piece_type: type,
                                    quantity: quantity,
                                    unit_price: Number.isFinite(unitPrice) ? unitPrice : 0,
                                    original_price: Number.isFinite(origPriceFree) ? origPriceFree : 0,
                                    promosId: Number.isFinite(Number(promosId)) ? Number(promosId) : 0,
                                    is_contra: isContractPriceApplied ? "Y" : "N",
                                    item_discount_type: discountTypeForAPI,
                                    promo_type: "FREE"
                                };
                                const effectiveDiscountFree = discountValue !== null ? discountValue : itemDiscount;
                                // Only set 100% discount to "0" for promotional discounts, not manual discounts
                                const isManualDiscountFree = discountValue !== null;
                                const hasPromotionAppliedFree = item.promotion_applied === true || promosId > 0;
                                const isPromo100PercentFree = !isManualDiscountFree && hasPromotionAppliedFree && discountTypeForAPI === "percentage" && (effectiveDiscountFree === 100 || effectiveDiscountFree === 100.0);
                                if (isPromo100PercentFree) {
                                    itemPayload.item_discount = "0";
                                } else {
                                    itemPayload.item_discount = String(effectiveDiscountFree);
                                }
                                if (orderQty !== null) {
                                    itemPayload.order_qty = orderQty;
                                }
                                const promoDiscAmountFree = item.promo_disc_amount !== undefined && item.promo_disc_amount !== null
                                    ? parseFloat(item.promo_disc_amount)
                                    : null;
                                if (promoDiscAmountFree !== null && Number.isFinite(promoDiscAmountFree)) {
                                    itemPayload.promo_disc_amount = promoDiscAmountFree;
                                }
                                if (item.promotion_applied === true) {
                                    itemPayload.promotion_applied = true;
                                }
                                const promotionIdFree = item.promotion_id !== undefined && item.promotion_id !== null
                                    ? Number(item.promotion_id)
                                    : null;
                                if (promotionIdFree !== null && Number.isFinite(promotionIdFree) && promotionIdFree > 0) {
                                    itemPayload.promotion_id = promotionIdFree;
                                }
                                let categoryNameFree = (item.category_name != null && item.category_name !== "") ? String(item.category_name).trim() : ((item.categorie_name != null && item.categorie_name !== "") ? String(item.categorie_name).trim() : "");
                                if (categoryNameFree) {
                                    itemPayload.category_name = categoryNameFree;
                                }
                                itemsMap.set(key, itemPayload);
                            }
                        }
                    }
                });
            }

            const promotionItems = Array.from(itemsMap.values());
            const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
            checkPromotionMutation.mutate({
                customer_id: customerId,
                order_date: orderDate,
                order_time: orderTime,
                channel: orderChannel,
                order_id: orderIdForPayload,
                items: promotionItems,
                over_all_discount: totalInvDiscPercent ?? 0
            }, {
                onSuccess: (data) => {
                    promotionCheckInProgressRef.current = false;
                    if (Array.isArray(data?.data?.items)) {
                        const currentCartItems = cartItemsRef.current;
                        const cartDataForMerge = {
                            data: currentCartItems.length > 0
                                ? currentCartItems
                                : transformProductDetailsToCart(originalOrderProducts),
                            success: true
                        };
                        const mergedItems = mergePromotionResponseWithCart(cartDataForMerge, data, originalOrderProducts, currentCartItems, isSingleOrderPickingRef.current);
                        setCartItems(mergedItems);

                        if (data?.data?.totals) {
                            setApiTotals({
                                ...data.data.totals,
                                bundle_discounts: data.data.bundle_discounts
                            });
                        }
                    }
                    setPromotionCheckQueue({});
                },
                onError: (error) => {
                    promotionCheckInProgressRef.current = false;
                    setSnackbar({ open: true, message: "Failed to check promotions", severity: "error" });
                    setPromotionCheckQueue({});
                }
            });
        }, 500);

        return () => {
            if (promotionCheckTimerRef.current) {
                clearTimeout(promotionCheckTimerRef.current);
            }
        };
    }, [promotionCheckQueue, orderHeader, customerId, checkPromotionMutation, orderChannel, originalOrderProducts, totalInvDiscPercent]);

    useEffect(() => {
        if (isError) {
            setSnackbar({ open: true, message: "Failed to load order", severity: "error" });
        }
    }, [isError]);

    useEffect(() => {
        cartItemsRef.current = cartItems;
    }, [cartItems]);

    useEffect(() => {
        return () => {
            if (allotQtyDebounceTimerRef.current) {
                clearTimeout(allotQtyDebounceTimerRef.current);
            }
            if (promotionCheckTimerRef.current) {
                clearTimeout(promotionCheckTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        calculateCartTotals();
    }, [cartItems, totalInvDiscPercent, calculateCartTotals]);

    useEffect(() => {
        const currentItems = cartItemsRef.current;
        if (currentItems.length === 0) {
            if (lastDiscountCalculationRef.current.totalDiscountAmount !== 0) {
                lastDiscountCalculationRef.current = { totalDiscountAmount: 0, itemsHash: "" };
            }
            return;
        }

        const itemsHash = currentItems.map(item =>
            `${item.id || item.product_id}_${item.price}_${item.qty}_${item.discount}_${item.discount_type || item.productWiseDiscountType || 'amount'}`
        ).join('|');

        const totalDiscountAmount = (() => {
            let subtotalAmount = 0;
            let vatAmount = 0;

            currentItems.forEach((item) => {
                const price = parseFloat(item.price) || 0;
                const qty = parseInt(item.qty) || 0;
                const discount = parseFloat(item.discount || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";

                const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

                let baseTotal = price * qty;

                let discountedTotal;
                if (isFixedDiscount) {
                    const discountAmount = parseFloat(discount) || 0;
                    discountedTotal = Math.max(0, baseTotal - discountAmount);
                } else {
                    const multiplier = Math.min(Math.max(discount, 0), 100);
                    discountedTotal = baseTotal * (1 - multiplier / 100);
                }

                let itemSubtotal;
                let itemVat;
                if (isVatInclusive && vatRate > 0) {
                    itemSubtotal = discountedTotal / (1 + vatRate / 100);
                    itemVat = discountedTotal - itemSubtotal;
                } else {
                    itemSubtotal = discountedTotal;
                    itemVat = itemSubtotal > 0 ? (itemSubtotal * vatRate) / 100 : 0;
                }

                subtotalAmount += itemSubtotal;
                vatAmount += itemVat;
            });

            const totalBeforeDiscount = subtotalAmount + vatAmount;
            return (totalBeforeDiscount * totalInvDiscPercent) / 100;
        })();

        const needsUpdate =
            Math.abs(lastDiscountCalculationRef.current.totalDiscountAmount - totalDiscountAmount) > 0.01 ||
            lastDiscountCalculationRef.current.itemsHash !== itemsHash;

        if (needsUpdate) {
            if (totalDiscountAmount > 0) {
                const updatedItems = calculateProportionalInvoiceDiscount(currentItems, totalDiscountAmount);
                setCartItems(updatedItems);
            } else {
                const hasNonZeroDiscount = currentItems.some(item => parseFloat(item.invoiceDiscountAmount || 0) > 0);
                if (hasNonZeroDiscount) {
                    setCartItems(currentItems.map(item => ({ ...item, invoiceDiscountAmount: 0 })));
                }
            }
            lastDiscountCalculationRef.current = {
                totalDiscountAmount,
                itemsHash
            };
        }
    }, [cartTotals.totalBeforeDiscount, cartTotals.discountAmount, totalInvDiscPercent, calculateProportionalInvoiceDiscount]);

    const handleCancel = () => {
        setCancelConfirmDialog(true);
    };

    const handleSave = () => {
        setSaveConfirmDialog(true);
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const showSnackbar = (message, severity = "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const validateQuantity = (value) => {
        if (!value || value === "") {
            showSnackbar("Please enter a valid quantity");
            return false;
        }
        const numValue = Number(value);
        if (!Number.isInteger(numValue) || numValue < 0) {
            showSnackbar("Please enter a valid quantity");
            return false;
        }
        if (numValue < 1 || numValue > 9999) {
            showSnackbar("Quantity must be between 1 and 9999");
            return false;
        }
        return true;
    };

    const handleDeleteItemClick = (itemId) => {
        setDeleteItemDialog({ open: true, itemId });
    };

    const handleConfirmDeleteItem = () => {
        if (deleteItemDialog.itemId) {
            handleDeleteItem(deleteItemDialog.itemId);
        }
        setDeleteItemDialog({ open: false, itemId: null });
    };

    const handleCloseDeleteItemDialog = () => {
        setDeleteItemDialog({ open: false, itemId: null });
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatTime = (date) => {
        const d = new Date(date);
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const extractDateAndTime = (dateString) => {
        if (!dateString) {
            const now = new Date();
            return {
                date: formatDateForAPI(now),
                time: formatTime(now)
            };
        }

        if (typeof dateString === 'string' && dateString.includes(' ')) {
            const parts = dateString.split(' ');
            const datePart = parts[0];
            const timePart = parts[1] || '00:00:00';
            return {
                date: datePart,
                time: timePart
            };
        }

        const d = new Date(dateString);
        return {
            date: formatDateForAPI(d),
            time: formatTime(d)
        };
    };

    const handleConfirmSave = () => {
        setSaveConfirmDialog(false);

        const userInfo = localStorage.getItem("userId");
        const userId = userInfo || "0";

        const currentDate = new Date();
        const orderDate = orderHeader?.order_date || formatDate(currentDate);
        const orderTime = orderHeader?.order_time || formatTime(currentDate);

        const promoTypeMap = new Map();
        cartItems.forEach((item) => {
            const productId = String(item.product_id || item.productId || "");
            const orderType = (item.type || item.order_type || "box").toLowerCase();
            let promoType = item.promo_type || item.promoType || item.promosType || null;
            if (!promoType && item.buyOrGet) {
                const buyOrGetUpper = String(item.buyOrGet).toUpperCase();
                promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
            }
            const key = `${productId}_${orderType}`;

            if (promoType === "BUY" || promoType === "GET" || promoType === "FREE") {
                if (!promoTypeMap.has(key)) {
                    promoTypeMap.set(key, new Set());
                }
                promoTypeMap.get(key).add(promoType);
            }
        });

        const orderList = cartItems.map((item) => {
            const qty = parseInt(item.qty) || 0;
            const price = parseFloat(item.price) || 0;
            const unitPrice = parseFloat(item.unit_price ?? item.price ?? 0);
            const originalPrice = parseFloat(item.original_price ?? item.unit_price ?? price ?? 0);
            const discount = parseFloat(item.discount || 0);
            const vatRate = parseFloat(item.rate || item.tax_rate || 0);
            const taxMethod = item.tax_method || "exclusive";
            const isVatInclusive = taxMethod === "inclusive";

            const isPromotionGenerated = item.generated === true || item.generated === "1" || item.generated === 1;
            const promotionId = item.promoId || item.promosId || null;
            const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;

            const promoDiscountPercentage = parseFloat(item.promo_disc_percentage ?? 0) || 0;
            const promoDiscountAmount = parseFloat(item.promo_disc_amount ?? 0) || 0;

            const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
            const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
            const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

            let finalSubtotal;
            let finalItemVat;
            let productWiseDiscountPercentage;
            let productWiseDiscountAmount;

            const baseTotal = unitPrice * qty;

            let discountedTotal;
            if (isFixedDiscount) {
                const discountAmount = parseFloat(discount) || 0;
                discountedTotal = Math.max(0, baseTotal - discountAmount);
                productWiseDiscountAmount = discountAmount;
                productWiseDiscountPercentage = baseTotal > 0 ? (discountAmount / baseTotal) * 100 : 0;
            } else {
                const multiplier = Math.min(Math.max(discount, 0), 100);
                discountedTotal = baseTotal * (1 - multiplier / 100);
                productWiseDiscountPercentage = discount;
                productWiseDiscountAmount = baseTotal - discountedTotal;
            }

            if (isVatInclusive && vatRate > 0) {
                finalSubtotal = discountedTotal / (1 + vatRate / 100);
                finalItemVat = discountedTotal - finalSubtotal;
            } else {
                finalSubtotal = discountedTotal;
                finalItemVat = finalSubtotal > 0 ? (finalSubtotal * vatRate) / 100 : 0;
            }
            const productId = item.product_id || item.productId || null;
            const productCode = item.code || item.product_code || null;
            const productName = item.name || item.product_name || item.desc || null;
            const size = item.size || null;
            const contractPriceApplied = item.isContractPriceApplied || null;
            const contractPrice = item.ContractPrice || null;

            const orderType = (item.type || item.order_type || "box").toLowerCase();
            let promoType = item.promo_type || item.promoType || item.promosType || null;
            if (!promoType && item.buyOrGet) {
                const buyOrGetUpper = String(item.buyOrGet).toUpperCase();
                promoType = buyOrGetUpper === "BUY" ? "BUY" : buyOrGetUpper === "GET" ? "GET" : buyOrGetUpper === "FREE" ? "FREE" : null;
            }
            const promotionType = promoType;
            const key = `${productId}_${orderType}`;
            const promoTypesForProduct = promoTypeMap.get(key);
            const hasBothBuyAndGet = promoTypesForProduct && promoTypesForProduct.has("BUY") && (promoTypesForProduct.has("GET") || promoTypesForProduct.has("FREE"));
            const isBuyItem = promoType === "BUY";

            const originalProduct = originalOrderProducts.find(
                p => String(p.product_id) === String(productId) &&
                    (p.order_type || "").toLowerCase() === orderType
            );
            let originalProductId = originalProduct?.id || item.original_product_id || null;

            if (hasBothBuyAndGet && isBuyItem) {
                originalProductId = null;
            }

            const id = originalProductId || null;
            let originalOrderQty = null;
            if (item.order_qty !== undefined && item.order_qty !== null && item.order_qty !== "") {
                originalOrderQty = String(item.order_qty);
            } else if (originalProduct?.order_qty !== undefined && originalProduct.order_qty !== null && originalProduct.order_qty !== "") {
                originalOrderQty = String(originalProduct.order_qty);
            } else if (originalProduct?.quantity !== undefined && originalProduct.quantity !== null && originalProduct.quantity !== "") {
                originalOrderQty = String(originalProduct.quantity);
            } else if (originalProduct?.qty !== undefined && originalProduct.qty !== null && originalProduct.qty !== "") {
                originalOrderQty = String(originalProduct.qty);
            }

            return {
                product_id: productId !== null ? String(productId) : null,
                id: id !== null ? String(id) : (hasBothBuyAndGet && isBuyItem ? "0" : null),
                product_code: productCode !== null ? String(productCode) : null,
                product_name: productName !== null ? String(productName) : null,
                unit_price: String(unitPrice.toFixed(2)),
                original_price: String(originalPrice.toFixed(2)),
                quantity: String(qty),
                unit_quantity: String(qty),
                discount: String(discount.toFixed(2)),
                order_type: (item.type || item.order_type || "box").toLowerCase(),
                order_qty: originalOrderQty !== null ? originalOrderQty : String(qty),
                item_tax: String(finalItemVat.toFixed(2)),
                subtotal: String(finalSubtotal.toFixed(2)),
                tax_rate_percent: String(vatRate),
                is_promoted: isPromoted ? "1" : "0",
                size: size !== null ? String(size) : null,
                picked_qty: "0.00",
                accept_qty: String(item.allot_qty !== undefined ? item.allot_qty : qty),
                proforma_qty: "0",
                item_discount: String(discount.toFixed(2)),
                productWiseDiscountType: isFixedDiscount ? "amount" : "percentage",
                productWiseDiscountPercentage: String(productWiseDiscountPercentage.toFixed(2)),
                productWiseDiscountAmount: String(productWiseDiscountAmount.toFixed(2)),
                promoDiscountPercentage: String(promoDiscountPercentage.toFixed(2)),
                promoDiscountAmount: String(promoDiscountAmount.toFixed(2)),
                deliver_qty: "0.00",
                isContractPriceApplied: contractPriceApplied !== null ? String(contractPriceApplied) : "N",
                ContractPrice: contractPrice !== null ? String(contractPrice) : null,
                promo_name: promotionType !== null ? String(promotionType) : null,
                free_product: isPromotionGenerated ? "Y" : "N",
                promo_product: isPromoted ? "Y" : "N",
                promo_id: promotionId !== null ? String(promotionId) : "0",
                promotion_applied: isPromoted ? "1" : "0",
                generated: isPromotionGenerated ? "1" : "0",
            };
        });

        const payload = {
            orderId: orderId,
            status: "Accept",
            group_id: String(orderHeader?.group_id || ""),
            delivery_address: deliveryAddress || "",
            grand_total: String(cartTotals.totalBeforeDiscount?.toFixed(2) || "0.00"),
            over_all_amount: String(cartTotals.discountAmount?.toFixed(2) || "0.00"),
            over_all_discount: String(totalInvDiscPercent || "0.00"),
            orderList: orderList,
            staffNote: staffNote || "",
            order_date: orderDate,
            order_time: orderTime,
            userId: String(userId),
        };

        updateInvoiceMutation.mutate(payload);
    };

    const handleCloseSaveConfirmDialog = () => {
        setSaveConfirmDialog(false);
    };

    const handleConfirmCancel = () => {
        setCancelConfirmDialog(false);
        clearCart();
        setTotalInvDiscPercent(0);
        navigate('/delivery/route_planing');
    };

    const handleCloseCancelConfirmDialog = () => {
        setCancelConfirmDialog(false);
    };

    const validatePrice = (value, item) => {
        if (!value || value === "") {
            showSnackbar("Please enter a valid price");
            return false;
        }
        if (item?.type === "FREE") {
            showSnackbar("Cannot edit price for free promotional items");
            return false;
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
            showSnackbar("Please enter a valid price (e.g., 45.99)");
            return false;
        }
        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        if (decimalPlaces > 2) {
            showSnackbar("Please enter a valid price (e.g., 45.99)");
            return false;
        }
        if (numValue < 0.01 || numValue > 9999.99) {
            showSnackbar("Price must be between £0.01 and £9,999.99");
            return false;
        }
        return true;
    };

    const validateDiscount = (value, discountType = "percentage", cartItem = null) => {
        if (value === "" || value === null || value === undefined) {
            return true;
        }

        const numValue = Number(value);

        if (isNaN(numValue)) {
            showSnackbar(discountType === "percentage" ? "Please enter a valid discount percentage" : "Please enter a valid discount amount");
            return false;
        }

        if (numValue < 0) {
            showSnackbar("Discount cannot be negative");
            return false;
        }

        if (discountType === "percentage") {
            if (numValue > 100) {
                showSnackbar("Discount cannot exceed 100%");
                return false;
            }
        } else {
            const isFixed = discountType === "amount" || discountType === "£" || discountType === "fixed";
            if (isFixed && cartItem) {
                const orderType = cartItem.type || cartItem.order_type || "Box";
                const qty = (cartItem.order_qty !== undefined && cartItem.order_qty !== null && cartItem.order_qty !== "")
                    ? parseQty(cartItem, cartItem.order_qty)
                    : parseQty(cartItem, cartItem.qty);
                let unitPrice = 0;
                if (cartItem.unit_price !== undefined && cartItem.unit_price !== null && cartItem.unit_price !== "") {
                    unitPrice = parseFloat(cartItem.unit_price);
                } else if (orderType === "Box") {
                    unitPrice = parseFloat(cartItem.sales_box_price || 0);
                } else {
                    unitPrice = parseFloat(cartItem.sales_piece_price || 0);
                }
                const lineTotal = unitPrice * qty;
                if (lineTotal > 0 && numValue >= lineTotal) {
                    showSnackbar(`Discount amount must be less than line total (£${lineTotal.toFixed(2)})`);
                    return false;
                }
            }
        }

        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        if (decimalPlaces > 2) {
            showSnackbar("Discount can have maximum 2 decimal places");
            return false;
        }

        return true;
    };

    const checkPriceEditPermission = () => {
        const hasPermission = true;
        if (!hasPermission) {
            showSnackbar("You don't have permission to edit prices. Contact your manager.");
            return false;
        }
        return true;
    };

    const handleValidatedQuantityChange = (cartItem, value, changeType = 'absolute') => {
        if (changeType === 'increment' || changeType === 'decrement') {
            const currentQty = parseInt(cartItem.qty) || 0;
            const finalQty = currentQty + value;

            if (finalQty < 1 || finalQty > 9999) {
                showSnackbar(`Quantity must be between 1 and 9999`, "warning");
                return;
            }
            handleQuantityChange(cartItem, value, changeType);
        } else {
            if (validateQuantity(value)) {
                handleQuantityChange(cartItem, value, changeType);
            } else {
                return;
            }
        }

        if (customerId) {
            setTimeout(() => {
                const currentCartItems = cartItemsRef.current;
                const productIds = currentCartItems.map(item => item.product_id || item.productId).filter(Boolean);
                const uniqueProductIds = [...new Set(productIds)];
                const newQueue = {};
                uniqueProductIds.forEach(pid => {
                    newQueue[pid] = Date.now();
                });
                setPromotionCheckQueue(prevQueue => ({ ...prevQueue, ...newQueue }));
            }, 0);
        }
    };

    const handleValidatedPriceChange = (cartItem, value, options = {}) => {
        if (!checkPriceEditPermission()) {
            return;
        }
        const itemDetails = cartItem;
        if (itemDetails?.is_promoted === "1" && itemDetails?.type === "FREE") {
            showSnackbar("Cannot edit price for free promotional items");
            return false;
        }

        if (options?.commit === false) {
            handlePriceChange(cartItem, value, options);
            return true;
        }

        if (validatePrice(value, cartItem)) {
            handlePriceChange(cartItem, value, options);
            return true;
        }

        return false;
    };

    const handleValidatedDiscountChange = (cartItem, value, discountType = "amount") => {
        if (validateDiscount(value, discountType, cartItem)) {
            handleDiscountChange(cartItem, value, discountType);
        }
    };

    const handleDiscountChangeComplete = useCallback((cartItem, value, discountType) => {
        if (customerId) {
            setTimeout(() => {
                const currentCartItems = cartItemsRef.current;
                const productIds = currentCartItems.map(item => item.product_id || item.productId).filter(Boolean);
                const uniqueProductIds = [...new Set(productIds)];
                const newQueue = {};
                uniqueProductIds.forEach(pid => {
                    newQueue[pid] = Date.now();
                });
                setPromotionCheckQueue(prevQueue => ({ ...prevQueue, ...newQueue }));
            }, 0);
        }
    }, [customerId]);

    const handleValidatedTotalInvDiscChange = (value) => {
        if (validateDiscount(value, "percentage", null)) {
            setTotalInvDiscPercent(value);
        }
    };

    const handleAllotQtyChange = useCallback((cartItem, value) => {
        if (value === "" || value === "." || value === null || value === undefined) {
            return;
        }

        const numValue = parseFloat(value);
        if (isNaN(numValue) || numValue < 0) {
            showSnackbar("Please enter a valid allot quantity", "error");
            return;
        }

        if (numValue > 9999) {
            showSnackbar("Allot Qty cannot exceed 9999", "error");
            return;
        }

        const itemId = cartItem.id || cartItem.product_id;
        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId || item.product_id === itemId) {
                return {
                    ...item,
                    allot_qty: value
                };
            }
            return item;
        }));

        if (customerId) {
            if (allotQtyDebounceTimerRef.current) {
                clearTimeout(allotQtyDebounceTimerRef.current);
            }

            allotQtyDebounceTimerRef.current = setTimeout(() => {
                const currentCartItems = cartItemsRef.current;
                const productIds = currentCartItems.map(item => item.product_id || item.productId).filter(Boolean);
                const uniqueProductIds = [...new Set(productIds)];
                const newQueue = {};
                uniqueProductIds.forEach(pid => {
                    newQueue[pid] = Date.now();
                });
                setPromotionCheckQueue(prevQueue => ({ ...prevQueue, ...newQueue }));
            }, 500);
        }
    }, [customerId, showSnackbar]);

    const openAddProduct = () => {
        setOpenAddDialog(true);
    };

    const closeAddProduct = () => {
        setOpenAddDialog(false);
    };

    const toNumber = (val) => {
        const n = Number(val);
        return isNaN(n) ? 0 : n;
    };

    const handleAddProductSave = ({ selectedProduct, selectedOrderType, price, quantity }) => {
        const p = toNumber(price) || 0;
        const q = Math.max(1, Math.floor(toNumber(quantity)));
        const orderTypeRaw = selectedOrderType.value;
        const orderType = orderTypeRaw.charAt(0).toUpperCase() + orderTypeRaw.slice(1).toLowerCase();
        const availableQtyForType = orderType === "Piece"
            ? (parseFloat(selectedProduct?.availablePieceQty ?? selectedProduct?.available_piece_qty ?? 0) || 0)
            : (parseFloat(selectedProduct?.availableBoxQty ?? selectedProduct?.available_box_qty ?? 0) || 0);

        if (shouldCheckStockBeforeAddToCart && availableQtyForType <= 0) {
            showSnackbar("Out of Stock", "warning");
            return;
        }

        if (q > 9999) {
            setSnackbar({ open: true, message: "Quantity cannot exceed 9999. Please reduce quantity before adding.", severity: "warning" });
            return;
        }

        setCartItems((prev) => {
            const existingItemIndex = prev.findIndex(
                item => (item.product_id === selectedProduct.id || item.productId === selectedProduct.id) && item.type === orderType
            );

            if (existingItemIndex !== -1) {
                const existingItem = prev[existingItemIndex];
                const currentQty = parseInt(existingItem.qty) || 0;

                if (currentQty >= 9999) {
                    setSnackbar({ open: true, message: "Quantity cannot exceed 9999. Please reduce quantity before adding more.", severity: "warning" });
                    return prev;
                }

                const newQty = currentQty + q;

                if (newQty > 9999) {
                    setSnackbar({ open: true, message: "Quantity cannot exceed 9999. Please reduce quantity before adding more.", severity: "warning" });
                    return prev;
                }

                const updatedRow = {
                    ...existingItem,
                    qty: String(newQty)
                };

                if (customerId) {
                    const productId = selectedProduct.id;
                    setPromotionCheckQueue(prev => ({
                        ...prev,
                        [productId]: Date.now()
                    }));
                }

                return [updatedRow, ...prev.filter((_, i) => i !== existingItemIndex)];
            }

            const idGen = `np_${Date.now()}`;
            const boxPrice = Number(selectedProduct.price) || 0;
            const piecePrice = Number(selectedProduct.split_price) || 0;
            const finalBoxPrice = Number(selectedProduct.final_price) || boxPrice;
            const finalPiecePrice = Number(selectedProduct.final_split_price) || piecePrice;
            const split = Number(selectedProduct.split) || 1;
            const vatRate = Number(selectedProduct.rate) || 0;
            const originalPrice = orderType === "Box" ? finalBoxPrice : finalPiecePrice;

            const newItem = {
                id: idGen,
                product_id: selectedProduct.id,
                productId: selectedProduct.id,
                code: selectedProduct.product_code,
                product_code: selectedProduct.product_code,
                name: selectedProduct.product_name,
                product_name: selectedProduct.product_name,
                desc: selectedProduct.product_name,
                type: orderType,
                order_type: orderType,
                split,
                sales_box_price: boxPrice,
                sales_piece_price: piecePrice,
                boxPrice,
                piecePrice,
                qty: String(q),
                price: p,
                unit_price: p,
                original_price: originalPrice,
                discount: "0",
                discount_type: "amount",
                rate: String(vatRate),
                tax_rate: String(vatRate),
                is_promoted: "0",
                noOfPieceInBox: split,
                price_1: Number(selectedProduct.price_1 ?? 0) || 0,
                price_2: Number(selectedProduct.price_2 ?? 0) || 0,
                price_3: Number(selectedProduct.price_3 ?? 0) || 0,
                price_4: Number(selectedProduct.price_4 ?? 0) || 0,
                price_5: Number(selectedProduct.price_5 ?? 0) || 0,
                piece_price1: Number(selectedProduct.piece_price1 ?? 0) || 0,
                piece_price2: Number(selectedProduct.piece_price2 ?? 0) || 0,
                piece_price3: Number(selectedProduct.piece_price3 ?? 0) || 0,
                piece_price4: Number(selectedProduct.piece_price4 ?? 0) || 0,
                piece_price5: Number(selectedProduct.piece_price5 ?? 0) || 0,
                cost: Number(selectedProduct.cost ?? 0) || 0,
                itemTax: 0,
                subtotal: 0,
                availableBoxQty: Number(selectedProduct.availableBoxQty ?? 0) || 0,
                availablePieceQty: Number(selectedProduct.availablePieceQty ?? 0) || 0,
                size: selectedProduct.size || "",
                inner_ean_number: selectedProduct.inner_ean_number || "",
                outer_ean_number: selectedProduct.outer_ean_number || "",
                innerEanNumber: selectedProduct.inner_ean_number || "",
                outerEanNumber: selectedProduct.outer_ean_number || "",
                discountedBoxPrice: 0,
                discountedSplitPrice: 0,
                productDiscountApplicable: Number(selectedProduct.productDiscountApplicable ?? 0) || 0,
                promoId: 0,
                promosId: 0,
                promoType: null,
                ...((selectedProduct.categorie_name || selectedProduct.category_name)
                    ? { category_name: String(selectedProduct.categorie_name || selectedProduct.category_name || "").trim() }
                    : {}),
                promosType: null,
                piece_cost: Number(selectedProduct.piece_cost ?? 0) || 0,
                pieceCost: Number(selectedProduct.piece_cost ?? 0) || 0,
                accept_qty: String(q),
                allot_qty: String(q)
            };

            if (customerId) {
                const productId = selectedProduct.id;
                setPromotionCheckQueue(prev => ({
                    ...prev,
                    [productId]: Date.now()
                }));
            }

            return [newItem, ...prev];
        });

        closeAddProduct();
        showSnackbar("Product added successfully", "success");
    };

    const handleBarcodeDetected = useCallback(
        async (barcodeValue) => {
            const trimmed = `${barcodeValue}`.trim();
            if (!trimmed || isBarcodeProcessingRef.current) return;
            if (!customerId) {
                setSnackbar({ open: true, message: "Please ensure customer is loaded before scanning", severity: "warning" });
                return;
            }
            isBarcodeProcessingRef.current = true;
            try {
                const response = await getProductsList({
                    pattern: trimmed,
                    customer_id: customerId,
                    orderChannel: orderChannel
                });
                const products = response?.product_details || [];
                const firstProduct = products[0];
                if (!firstProduct) {
                    setSnackbar({ open: true, message: "No product found for scanned code", severity: "warning" });
                    return;
                }
                const split = Number(firstProduct.split) ?? 1;
                let orderTypeValue = "Box";
                if (split === 1) {
                    orderTypeValue = "Piece";
                } else if (split === 2) {
                    orderTypeValue = "Box";
                }
                const selectedOrderType = { id: split === 1 ? 1 : 0, title: orderTypeValue, value: orderTypeValue };
                const boxPrice = Number(firstProduct.final_price ?? firstProduct.price) || 0;
                const piecePrice = Number(firstProduct.final_split_price ?? firstProduct.split_price) || 0;
                const price = orderTypeValue === "Piece" ? piecePrice : boxPrice;
                handleAddProductSave({
                    selectedProduct: firstProduct,
                    selectedOrderType,
                    price: price > 0 ? price.toFixed(2) : "0.00",
                    quantity: "1"
                });
            } catch {
                setSnackbar({ open: true, message: "Unable to fetch product for scanned code", severity: "error" });
            } finally {
                isBarcodeProcessingRef.current = false;
            }
        },
        [customerId, orderChannel, handleAddProductSave]
    );

    const isLoadingData = isLoading;

    return (
        <>
            <Box sx={{ p: 1, backgroundColor: '#f5f7fa' }}>
                <Paper variant="elevation" elevation={2} sx={{ p: 1, overflowX: 'hidden' }}>
                    {isLoadingData && (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                            <Typography>Loading order details...</Typography>
                        </Box>
                    )}
                    {!isLoadingData && (
                        <>

                            <Grid2 container spacing={2} sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row", md: "row" },
                                justifyContent: "space-between",
                                alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
                                gap: { xs: 1, sm: 1, md: 2 }
                            }}>
                                <Grid2 size={{ xs: 12, md: 4 }}>
                                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
                                        Edit Invoice
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 6 }} gap={{ xs: 1, sm: 1, md: 2 }} display="flex" alignItems={{ xs: "flex-start", sm: "flex-start", md: "center" }} direction={{ xs: "column", sm: "column", md: "row" }}>
                                    <Typography sx={{ fontSize: "0.8rem" }}>
                                        <span style={{ fontWeight: 600 }}>Customer Name:</span> {orderHeader?.company || orderHeader?.name || ""}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8rem" }}>
                                        <span style={{ fontWeight: 600 }}>Account No:</span> {orderHeader?.account_no || ""}
                                    </Typography>
                                </Grid2>

                                <Grid2 size={{ xs: 12, md: 2 }} display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                                    <BarcodeScanner
                                        onDetected={handleBarcodeDetected}
                                        debounceInterval={600}
                                        label="Scan to add"
                                    />
                                    <Button
                                        variant="contained"
                                        className="button-variant-contained"
                                        startIcon={<Iconify icon="mdi:plus" width={20} height={20} />}
                                        onClick={openAddProduct}
                                        sx={{
                                            width: { xs: "100%", sm: "100%", md: "auto" }
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                                            Add New Product
                                        </Typography>
                                    </Button>
                                </Grid2>
                            </Grid2>
                            <Grid2 size={{ xs: 12, md: 12 }}>
                                <CartTable
                                    cartItems={cartItems}
                                    onTypeChange={handleTypeChange}
                                    onQuantityChange={handleValidatedQuantityChange}
                                    onPriceChange={handleValidatedPriceChange}
                                    onDiscountChange={handleValidatedDiscountChange}
                                    onDeleteItem={handleDeleteItemClick}
                                    showVatAsAmount={true}
                                    isEditMode={true}
                                    onValidationError={showSnackbar}
                                    getItemDetails={getItemDetails}
                                    customerId={selectedCustomer?.id}
                                    isLoading={checkPromotionMutation.isPending}
                                    onAllotQtyChange={handleAllotQtyChange}
                                    onDiscountChangeComplete={handleDiscountChangeComplete}
                                    onPriceChangeComplete={handlePriceChangeComplete}
                                />
                                <CartSummary
                                    cartTotals={cartTotals}
                                    totalInvDiscPercent={totalInvDiscPercent}
                                    onTotalInvDiscChange={handleValidatedTotalInvDiscChange}
                                    isEditMode={true}
                                />
                                <Grid2 container spacing={2} display="flex" flexDirection={{ xs: "column", sm: "column", md: "row" }} alignItems="center" justifyContent="center" mt={2}>
                                    <Grid2 size={{ xs: 12, md: 4 }}>
                                        <TextField className="input-border" variant="outlined" label="Delivery Address" placeholder="Enter Delivery Address" size="small" fullWidth multiline rows={1} InputLabelProps={{ shrink: true }} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 4 }}>
                                        <TextField className="input-border" 
                                        variant="outlined" label="Staff Note"
                                         placeholder="Enter Staff Note" 
                                         
                                         size="small" fullWidth multiline rows={1} 
                                         InputLabelProps={{ shrink: true }} 
                                         value={staffNote} onChange={(e) => setStaffNote(e.target.value)} />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 4 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
                                            <Button
                                                variant="outlined"
                                        
                                                className="button-variant-outlined"
                                                onClick={handleCancel}>Cancel</Button>
                                            <Button
                                                variant="contained"
                                                className="button-variant-contained"
                                                onClick={handleSave}>Confirm & Submit
                                            </Button>
                                        </Box>
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        </>
                    )}
                </Paper>
            </Box>
            <AddProductDialog
                open={openAddDialog}
                onClose={closeAddProduct}
                customerId={selectedCustomer?.id}
                orderChannel={orderChannel}
                onSave={handleAddProductSave}
            />

            <ConfirmDialog
                open={saveConfirmDialog}
                onClose={handleCloseSaveConfirmDialog}
                title="Confirm Order"
                content={
                    <Typography>
                        Do you really want to submit this order?
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"

                        onClick={handleConfirmSave}
                        disabled={updateInvoiceMutation.isPending}
                        sx={{
                            ml: 1,
                            backgroundColor: "#F8991D",
                            "&:hover": {
                                backgroundColor: "#F8991D",
                                boxShadow: "none",
                            },
                        }}
                    >
                        {updateInvoiceMutation.isPending ? "Submitting..." : "Confirm"}
                    </Button>
                }
            />

            <ConfirmDialog
                open={cancelConfirmDialog}
                onClose={handleCloseCancelConfirmDialog}
                title="Discard Changes"
                content={
                    <Typography>
                        Do you really want to cancel and discard all changes?
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmCancel}
                        sx={{ ml: 1 }}
                    >
                        Discard
                    </Button>
                }
            />

            <ConfirmDialog
                open={deleteItemDialog.open}
                onClose={handleCloseDeleteItemDialog}
                title="Delete Item"
                content="Do you really want to delete this item?"
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDeleteItem}
                        sx={{ ml: 1 }}
                    >
                        Delete
                    </Button>
                }
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditProforma;


